const HTML_TAG_REGEX = /<\/?[a-z][^>]*>/i;

const ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'STRONG',
  'EM',
  'U',
  'S',
  'UL',
  'OL',
  'LI',
  'A',
  'BLOCKQUOTE',
]);

const TAG_REPLACEMENTS = {
  B: 'STRONG',
  I: 'EM',
  DIV: 'P',
  SPAN: null,
  STRIKE: 'S',
};

const ALLOWED_ATTRIBUTES = {
  A: ['href'],
};

const SAFE_URL_REGEX = /^(https?:\/\/)/i;

export const escapeHtml = value =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const convertPlainTextToHtml = value => {
  if (!value) return '';
  const sanitizedValue = escapeHtml(value).replace(/\r/g, '');
  const paragraphs = sanitizedValue.split(/\n{2,}/).map(paragraph => paragraph.replace(/\n/g, '<br />'));
  return paragraphs.map(paragraph => `<p>${paragraph || '<br />'}</p>`).join('');
};

const fallbackSanitize = value => escapeHtml(value).replace(/\r?\n/g, '<br />');

const cleanAttributes = node => {
  const tag = node.nodeName;
  const allowed = ALLOWED_ATTRIBUTES[tag] || [];
  Array.from(node.attributes).forEach(attr => {
    const name = attr.name.toLowerCase();
    if (!allowed.includes(name)) {
      node.removeAttribute(attr.name);
      return;
    }
    if (tag === 'A' && name === 'href') {
      const href = attr.value.trim();
      if (!SAFE_URL_REGEX.test(href)) {
        node.removeAttribute(attr.name);
      } else {
        node.setAttribute('href', href);
        if (!node.getAttribute('rel')) {
          node.setAttribute('rel', 'noopener noreferrer');
        }
        if (!node.getAttribute('target')) {
          node.setAttribute('target', '_blank');
        }
      }
    }
  });
};

const cleanNode = (parent, doc) => {
  Array.from(parent.childNodes).forEach(child => {
    if (child.nodeType === Node.COMMENT_NODE) {
      parent.removeChild(child);
      return;
    }

    if (child.nodeType === Node.TEXT_NODE) {
      return;
    }

    if (child.nodeType !== Node.ELEMENT_NODE) {
      parent.removeChild(child);
      return;
    }

    let tagName = child.nodeName;
    if (Object.prototype.hasOwnProperty.call(TAG_REPLACEMENTS, tagName)) {
      const replacement = TAG_REPLACEMENTS[tagName];
      if (!replacement) {
        while (child.firstChild) {
          parent.insertBefore(child.firstChild, child);
        }
        parent.removeChild(child);
        return;
      }
      if (replacement !== tagName) {
        const replacementNode = doc.createElement(replacement.toLowerCase());
        Array.from(child.childNodes).forEach(nodeChild => {
          replacementNode.appendChild(nodeChild);
        });
        parent.replaceChild(replacementNode, child);
        child = replacementNode;
        tagName = child.nodeName;
      }
    }

    if (!ALLOWED_TAGS.has(tagName)) {
      while (child.firstChild) {
        parent.insertBefore(child.firstChild, child);
      }
      parent.removeChild(child);
      return;
    }

    cleanAttributes(child);
    cleanNode(child, doc);

    if (tagName === 'A' && !child.getAttribute('href')) {
      while (child.firstChild) {
        parent.insertBefore(child.firstChild, child);
      }
      parent.removeChild(child);
      return;
    }

    if ((tagName === 'P' || tagName === 'LI' || tagName === 'BLOCKQUOTE') && !child.textContent.trim()) {
      const hasLineBreak = Array.from(child.childNodes).some(nodeChild => nodeChild.nodeName === 'BR');
      if (!hasLineBreak) {
        parent.removeChild(child);
      }
    }
  });
};

export const sanitizeRichText = value => {
  if (!value) return '';
  const input = String(value);
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return fallbackSanitize(input);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(input, 'text/html');
  cleanNode(doc.body, doc);
  return doc.body.innerHTML;
};

export const richTextToPlainText = value => {
  if (!value) return '';
  const input = String(value);
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return input.replace(/<\/?[^>]+(>|$)/g, ' ');
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(input, 'text/html');
  return doc.body.textContent || '';
};

export const hasRichTextContent = value => richTextToPlainText(value).replace(/\s+/g, '').length > 0;

export const normalizeRichTextInput = value => {
  if (!value) return '';
  const input = String(value);
  const htmlCandidate = HTML_TAG_REGEX.test(input) ? input : convertPlainTextToHtml(input);
  const sanitized = sanitizeRichText(htmlCandidate);
  return hasRichTextContent(sanitized) ? sanitized : '';
};

export const ensureRichText = value => {
  if (!value) return '';
  return normalizeRichTextInput(String(value));
};
